import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip } from "@amzn/stencil-react-components/chip";
import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Col, Flex } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { Select } from "@amzn/stencil-react-components/select";
import Input from "@amzn/uxm-stencil-react-components/lib/src/stencil/input";
import { SearchField } from "@amzn/uxm-stencil-react-components/lib/src/stencil/search-field";
import TextArea from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { ListType } from "common/context/lists";
import { useLists } from "common/context/lists/ListsContext";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useSearchExperience } from "hooks";
import { useAIForMeData } from "hooks/useAIForMeData";
import { debounce } from "lib/helpers";
import { Visibility } from "lib/models/list-models";
import { ItemModel } from "lib/services/catalogical-models";
import { CatalogicalSearchRequestContent, SearchExperienceDomains } from "lib/services/shop-search-service";
import React, { FunctionComponent, useEffect, useMemo, useState } from "react";

import { buildJobFamilyRule, getJobFamilyFromRule } from "../../helpers/lists-helper";
import { calculateItemsDiff } from "../../helpers/lists-helper";

interface ListAdminModalProps {
  onClose: () => void;
  initialData?: {
    name?: string;
    description?: string;
    allowedDomains?: string[];
    items?: ItemModel[];
    evaluationRules?: DocumentType;
  };
  listId?: string;
  isEdit?: boolean;
  jobFamiliesProp?: string[];
  domains: string[];
}

const ITEM_DISPLAY_FIELD: Record<string, keyof ItemModel> = {
  aiforme: "title",
  software: "name",
};

const ListAdminModal: FunctionComponent<ListAdminModalProps> = ({
  onClose,
  initialData,
  listId,
  isEdit = false,
  jobFamiliesProp,
  domains,
}) => {
  const [name, setName] = useState(initialData?.name || getString("aiForMe.recommendedToolsFor", { jobFamily: "" }));
  const [description, setDescription] = useState(initialData?.description || "");
  const [selectedJobFamily, setSelectedJobFamily] = useState(getJobFamilyFromRule(initialData?.evaluationRules) || "");
  const [query, setQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState<ItemModel[]>(initialData?.items || []);
  const [showDropdown, setShowDropdown] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const { items, isLoading, searchCatalogItems } = useSearchExperience<ItemModel>(
    domains[0] as SearchExperienceDomains | "aiforme"
  );
  const { jobFamilies } = useAIForMeData();
  const { createList, updateList, getPublicLists, addItemToList, removeItemFromList } = useLists();
  const { addMessageBanner } = useMessageBannersBox();

  const availableJobFamilies = jobFamilies.filter((jobFamily) => !jobFamiliesProp?.includes(jobFamily));

  const getSearchResultsDebounced = useMemo(
    () =>
      debounce(async (searchText: string) => {
        const nextRequest: CatalogicalSearchRequestContent = {
          searchQuery: searchText,
          useSemanticSearch: false,
          limit: 5,
          offset: 0,
          vectorField: "embeddings", // TODO: Move to Search service domain specific config
        };
        searchCatalogItems(nextRequest);
        return [];
      }, 1000)[0],
    []
  );

  useEffect(() => {
    if (query && query != "") getSearchResultsDebounced(query);
  }, [query]);

  const handleSubmit = async () => {
    setIsProcessing(true);
    const data: any = {
      name,
      description,
      visibility: Visibility.PUBLIC,
      allowedDomains: domains,
      evaluationRules: buildJobFamilyRule(selectedJobFamily),
      itemIds: selectedItems.map((item) => item.taxonomyId),
    };

    const itemsDiff = calculateItemsDiff(selectedItems, initialData?.items);

    try {
      if (isEdit && listId) {
        await updateList(listId, data, ListType.PUBLIC_LISTS);
        if (itemsDiff.added.length > 0) {
          await addItemToList(
            itemsDiff.added.map((item) => item.taxonomyId),
            { listId },
            ListType.PUBLIC_LISTS
          );
        }
        if (itemsDiff.removed.length > 0) {
          await Promise.all(
            itemsDiff.removed.map((item) => removeItemFromList(listId, item.taxonomyId, ListType.PUBLIC_LISTS))
          );
        }
        addMessageBanner(getString("lists.modal.updateSuccess"), MessageBannerType.Success);
      } else {
        await createList(data, ListType.PUBLIC_LISTS);
        addMessageBanner(getString("lists.modal.createSuccess"), MessageBannerType.Success);
      }
      getPublicLists({ includeItemDetails: true });
      onClose();
    } catch (error) {
      addMessageBanner(
        getString(isEdit ? "lists.modal.updateError" : "lists.modal.createError"),
        MessageBannerType.Error
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const selectItem = (result: ItemModel) => {
    setSelectedItems((prev) => (prev.some((item) => item.taxonomyId === result.taxonomyId) ? prev : [...prev, result]));
    setShowDropdown(false);
  };

  const removeItem = (result: ItemModel) => {
    setSelectedItems((prev) => prev.filter((item) => item.taxonomyId !== result.taxonomyId));
  };

  const renderResultsText = (result: ItemModel) => {
    const field = ITEM_DISPLAY_FIELD[domains[0]] || "name";
    return result[field] as string;
  };

  const isValid = name.trim().length > 0;

  return (
    <ModalContent
      titleText={getString(isEdit ? "lists.modal.editTitle" : "lists.modal.title")}
      maxWidth="800px"
      buttons={[
        <Button key="cancel" variant={ButtonVariant.Tertiary} onClick={onClose} disabled={isProcessing}>
          {getString("lists.modal.cancel")}
        </Button>,
        <Button
          key="submit"
          variant={ButtonVariant.Primary}
          disabled={!isValid || isProcessing}
          onClick={handleSubmit}
          data-cy="list-submit-button"
        >
          {getString(isEdit ? "lists.modal.update" : "lists.modal.create")}
        </Button>,
      ]}
    >
      <Col gridGap="S300" width={"500px"}>
        <InputWrapper id="list-name" labelText={getString("lists.modal.name")} required={true}>
          {(inputProps) => (
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={getString("lists.modal.namePlaceholder")}
              touchpoint="list-name-input"
              data-cy="list-name-input"
              {...inputProps}
            />
          )}
        </InputWrapper>

        <InputWrapper id="list-job-family" labelText={getString("lists.modal.jobFamily")}>
          {(inputProps) => (
            <Select
              options={availableJobFamilies}
              isMulti={false}
              value={selectedJobFamily}
              data-testid="sel_jobFamilies"
              isSearchable
              onChange={(option) => {
                setSelectedJobFamily(option);
                if (name === getString("aiForMe.recommendedToolsFor", { jobFamily: "" })) {
                  setName(getString("aiForMe.recommendedToolsFor", { jobFamily: option }));
                }
              }}
              {...inputProps}
            />
          )}
        </InputWrapper>

        <InputWrapper id="fruit-selector-4" labelText={getString("lists.modal.search")}>
          {(inputProps) => (
            <>
              <SearchField
                touchpoint="seach-input"
                data-cy="search-input"
                data-testid="search-field"
                query={query}
                onChange={(q) => {
                  setQuery(q);
                  setShowDropdown(true);
                }}
                renderResult={showDropdown ? renderResultsText : undefined}
                results={showDropdown ? items || [] : []}
                onResultClick={(result) => {
                  selectItem(result);
                  setQuery("");
                }}
                shouldShowListOnNoResults={showDropdown}
                isLoading={showDropdown ? isLoading : false}
                {...inputProps}
              />
              <Flex flexDirection="row" gridGap={"S200"}>
                {selectedItems.map((item) => (
                  <Chip key={item.taxonomyId} onDismiss={() => removeItem(item)}>
                    {renderResultsText(item)}
                  </Chip>
                ))}
              </Flex>
            </>
          )}
        </InputWrapper>

        <InputWrapper id="list-description" labelText={getString("lists.modal.description")}>
          {(inputProps) => (
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={getString("lists.modal.descriptionPlaceholder")}
              rows={3}
              touchpoint="list-description-textarea"
              data-cy="list-description-textarea"
              {...inputProps}
            />
          )}
        </InputWrapper>
      </Col>
    </ModalContent>
  );
};

export default ListAdminModal;
